import DownloadButton from "components/DownloadButton";
import { toast } from "components/toastNotification";
import { useState } from "react";
import { CSVLink } from "react-csv";
import { downloadCDRCStatusReconciliationFindings } from "../api/downloadCDRCStatusReconciliationFindings";

interface ProducerCodeLogsContentModalViewProps {
  logsContent: string;
  notificationMessage: string;
  batchId?: number | null;
  isCDRCStatusReconciliation?: boolean;
}

export const ProducerCodeLogsContentModalView = ({
  logsContent,
  notificationMessage,
  batchId,
  isCDRCStatusReconciliation = false,
}: ProducerCodeLogsContentModalViewProps) => {
  const [exportFindings, setExportFindings] = useState<any[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Deduplicate and clean logsContent
  const logs = Array.from(new Set((logsContent ?? "").split("\n")))
    .filter((item: any) => item.trim() !== "")
    .join("\n");

  //#region Export Existing Findings To CSV
  const exportFailedFindings = (findings: string) => {
    if (!findings) {
      setIsDownloading(false);

      toast.fire(
        "Download Findings",
        "No findings are available for download.",
        "warning",
      );

      return;
    }

    const findingsArray = Array.from(new Set(findings.split("\n")))
      .filter((item: any) => item.trim() !== "")
      .map((item: any) => {
        const cleaned = item.replace("\r", "").trim();

        const separatorIndex = cleaned.indexOf(": ");

        const DateTime =
          separatorIndex >= 0 ? cleaned.substring(0, separatorIndex) : "";

        const fullMessage =
          separatorIndex >= 0 ? cleaned.substring(separatorIndex + 2) : cleaned;

        // .+ gets everythings after equals
        const producerMatch = fullMessage.match(/producer\s*code\s*=\s*(.+)/i);

        let message = fullMessage.trim();

        const messageSeparatorIndex = fullMessage.indexOf("=");

        if (messageSeparatorIndex >= 0) {
          message = fullMessage.substring(0, messageSeparatorIndex).trim();
        }

        return {
          DateTime,
          Message: message,
          "Producer Code": producerMatch ? producerMatch[1] : null,
        };
      });

    setExportFindings(findingsArray);

    toast.fire(
      "Download Findings",
      "Findings downloaded successfully.",
      "success",
    );

    setIsDownloading(false);
  };
  //#endregion

  //#region Download CDRC Status Reconciliation Findings
  const downloadCDRCStatusFindings = async () => {
    if (!batchId || batchId <= 0) {
      toast.fire("Download Findings", "Invalid reconciliation batch.", "error");

      return;
    }

    try {
      setIsDownloading(true);

      const response = await downloadCDRCStatusReconciliationFindings(batchId);

      if (!response?.data) {
        toast.fire(
          "Download Findings",
          "No findings are available for download.",
          "warning",
        );

        return;
      }

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

      if (blob.size === 0) {
        toast.fire(
          "Download Findings",
          "No findings are available for download.",
          "warning",
        );

        return;
      }

      const contentDisposition = response.headers?.["content-disposition"];

      let fileName = `CDRCStatusReconciliation_Findings_Batch_${batchId}.xlsx`;

      if (contentDisposition) {
        const utf8Match = contentDisposition.match(
          /filename\*\s*=\s*UTF-8''([^;]+)/i,
        );

        const normalMatch = contentDisposition.match(
          /filename\s*=\s*"?([^";]+)"?/i,
        );

        const returnedFileName = utf8Match?.[1] || normalMatch?.[1];

        if (returnedFileName) {
          try {
            fileName = decodeURIComponent(returnedFileName);
          } catch {
            fileName = returnedFileName;
          }
        }
      }

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute("download", fileName);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

      toast.fire(
        "Download Findings",
        "Findings downloaded successfully.",
        "success",
      );
    } catch {
      toast.fire("Download Findings", "Unable to download findings.", "error");
    } finally {
      setIsDownloading(false);
    }
  };
  //#endregion

  //#region CSVReport
  const findingsReport = {
    filename:
      notificationMessage +
      "Findings_" +
      new Date().toLocaleDateString() +
      ".csv",

    data: exportFindings ?? [],
  };
  //#endregion

  return (
    <div className="h-full">
      <div className="flex items-center justify-between pb-1 mt-2">
        <div className="font-bold text-primary text-base">FINDINGS</div>

        {isCDRCStatusReconciliation ? (
          <DownloadButton
            disabled={isDownloading}
            buttonValue={isDownloading ? "Downloading..." : "Download Findings"}
            onClick={downloadCDRCStatusFindings}
          />
        ) : (
          <CSVLink {...findingsReport}>
            <DownloadButton
              disabled={isDownloading}
              buttonValue={
                isDownloading ? "Downloading..." : "Download Findings"
              }
              onClick={() => {
                setIsDownloading(true);

                exportFailedFindings(logsContent);
              }}
            />
          </CSVLink>
        )}
      </div>

      <div className="bg-bgDB py-4 border-gray-700 h-full">
        <textarea
          name="logs"
          className="w-full h-[60vh] resize-none text-sm border-2 border-gray-200 p-3 font-mono"
          value={logs}
          disabled
        />
      </div>
    </div>
  );
};
